import { useState, useEffect, useCallback } from "react";
import { Input } from "shared/ui/Input";
import { Button } from "shared/ui/button";
import { useHttpRequest } from "shared/lib/hooks/useHttpRequest";
import { formatTime } from "shared/lib/hooks";
import styles from "../FormStyles.module.scss";

export const EmailConfirmForm = ({
  title,
  email,
  onSuccess,
  requestFn,
  onTokenReceived,
  onResendCode,
}) => {
  const { sendRequest, loading, error, clearError } = useHttpRequest(requestFn);

  const [canResend, setCanResend] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);

  const [formData, setFormData] = useState({
    verifyCode: "",
  });

  const [fieldErrors, setFieldErrors] = useState({
    verifyCode: false,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (value.trim() !== "") {
      setFieldErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEmpty = formData.verifyCode.trim() === "";

    setFieldErrors({
      verifyCode: isEmpty,
    });

    if (isEmpty || loading) return;

    const submitData = {
      email: email,
      verifyCode: formData.verifyCode.replace(/\s/g, ""),
    };

    try {
      const response = await sendRequest(submitData);

      if (response?.token) {
        onTokenReceived?.(response.token);
      }
      onSuccess?.();
    } catch (error) {
      const status = error?.status;

      if (status === 401) {
        return;
      }

      if (status >= 500) {
        return;
      }
    }
  };

  useEffect(() => {
    setCanResend(false);
    setTimeLeft(60);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleResend = useCallback(async () => {
    if (!canResend || !onResendCode) return;

    try {
      setCanResend(false);
      setTimeLeft(60);

      await onResendCode();

      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error resending code:", error);
    }
  }, [canResend, onResendCode]);

  return (
    <div className={styles.formWrapper}>
      <div className={styles.formWrapper__topBlock}>
        <p className={styles.formWrapper__title}>{title}</p>
        <p
          className={styles.formWrapper__text}
        >{`На вашу почту  ${email}  было отправлено письмо с кодом для завершения регистрации.`}</p>
      </div>
      <div className={styles.formWrapper__form}>
        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>Код из письма</p>
          <Input
            type="text"
            value={formData.verifyCode}
            onChange={(e) => handleInputChange("verifyCode", e.target.value)}
            placeholder="--- ---"
            className={`${fieldErrors.verifyCode ? styles.formWrapper__inputError : styles.formWrapper__input}`}
            mask="___ ___"
            replacement={{ _: /\d/ }}
          />
        </div>
        {Object.values(fieldErrors).some(Boolean) && (
          <span className={styles.formWrapper__greenText}>
            Не заполнено обязательное поле
          </span>
        )}
        {error && (
          <span className={styles.formWrapper__greenText}>
            Произошла ошибка, попробуйте позже
          </span>
        )}
      </div>
      <div className={styles.formWrapper__buttonBlock}>
        <div className={styles.formWrapper__button}>
          <Button variant="primary" size="large" onClick={handleSubmit}>
            Подтвердить
          </Button>
        </div>
        <div
          className={
            canResend
              ? styles.formWrapper__greyTextPointer
              : styles.formWrapper__greyText
          }
          onClick={canResend ? handleResend : undefined}
        >
          {canResend
            ? "Отправить код заново"
            : `Отправить код заново (${formatTime(timeLeft)})`}
        </div>
      </div>
    </div>
  );
};
